import {
  Box,
  Button,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { KeyboardDatePicker, KeyboardTimePicker } from "@material-ui/pickers";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import apiHelper from "../../../../util/ApiHelper";
import { useLoading } from "../../../../util/LoadingProvider";
import UploadImage from "../../UploadImage";
import dayjs from "dayjs";
import { modal } from "../../../../util";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import Select from "@material-ui/core/Select";

const useStyles = makeStyles((theme) => ({
  innerBox: {
    background: "#ffffff",
    marginBottom: theme.spacing(4),
    "& *": {
      fontSize: "13px",
      lineHeight: "15px",
    },
  },
  sectionBox: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: "300",
    fontSize: "13px",
    lineHeight: "15px",
    color: "#333333",
  },
  itemTitle: {
    fontWeight: "bold",
    fontSize: "12px",
    lineHeight: "14px",
    color: "#333333",
  },
  itemValue: {
    fontWeight: "normal",
    fontSize: "14px",
    lineHeight: "16px",
    color: "#333333",
  },
  input: {
    width: "100%",
    "& button": {
      fontSize: "13px",
      lineHeight: "15px",
      width: "25%",
    },
  },
  buttonGroup: {
    textAlign: "right",
    marginTop: "-20px",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  customLabel: {
    margin: "0 !important",
    "& label": {
      color: "black",
      fontWeight: 700,
      fontSize: "16px",
    },
    "& p": {
      fontSize: "12px",
    },
  },
}));

// const defaultFormValues = {
//   createTime: dayjs().format("YYYY-MM-DDTHH:mm:ss[Z]"),
//   displayDatetime: null,
//   hkiffId: null,
//   id: null,
//   localizations: {
//     en: {
//       title: "",
//       content: "",
//     },
//     tc: {
//       title: "",
//       content: "",
//     },
//   },
//   sequence: null,
//   status: {
//     deleted: false,
//     status: null,
//   },
//   image: "",
//   updateTime: dayjs().format("YYYY-MM-DDTHH:mm:ss[Z]"),
//   url: "",
//   // to initialize during fetch and
//   // to purge during submission
//   date: dayjs().format("YYYY-MM-DD"),
//   time: Date.now(),
// };

const defaultFormValues = {
  hkiffId: process.env.REACT_APP_HKIFF_ID_NO,
  regionId: "",
  localizations: {
    en: {
      name: "",
    },
    tc: {
      name: "",
    },
  },
  status: {
    status: true,
    deleted: false,
  },
  updateTime: dayjs().format("YYYY-MM-DDTHH:mm:ss[Z]"),
};

function DistrictEditPage({ disabled, ...props }) {
  const classes = useStyles();
  const { match, history } = props;
  const { id } = match.params;
  const [paramId, setParamId] = React.useState(id);
  const [defaultValue, setDefaultValue] = React.useState(defaultFormValues);
  const { setLoading } = useLoading();
  const [regionList, setRegionList] = React.useState([]);

  const { control, handleSubmit, getValues, setValue, reset } = useForm({
    defaultValues: React.useMemo(() => defaultValue, [defaultValue]),
  });

  const onSubmit = async (value) => {
    setLoading(true);
    // purge date and time
    const { date, time, ...data } = value;
    // regionId comes from validated form value
    console.log(data);

    const displayDatetime = `${date} ${dayjs(time).format("HH:mm:ss")}`;
    data.displayDatetime = displayDatetime;
    try {
      if (paramId !== "new") {
        const result = await apiHelper.hkiff.updateDistrictById(paramId, data);
        if (result.status === 200) {
          toast.dark("District is updated.");
        }
      } else {
        const result = await apiHelper.hkiff.createDistrict(data);
        if (result.status === 200) {
          setParamId(result.data.id);
          history.push(`/hkiff/region-management/district/${result.data.id}`);
          toast.dark("District is created.");
        }
      }
    } catch (e) {
      console.log(e);
    }
    setLoading(false);
  };

  const getRegion = React.useCallback(async () => {
    try {
      setLoading(true);
      const regions = await apiHelper.hkiff.getAllRegions();
      setRegionList(regions.data);
      console.log(regions.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const getDistrictById = React.useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiHelper.hkiff.getDistrictById(id);
      // initialize date and time
      console.log(result);
      const data = result.data;
      setDefaultValue(data);
      reset(data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [id, reset, setLoading]);

  React.useEffect(() => {
    if (id !== "new") getDistrictById();
    getRegion();
  }, [getRegion, getDistrictById, id]);

  const deleteDistrict = React.useCallback(async (id) => {
    const decision = await modal.fire({
      title: "Delete District",
      text: "Are you sure to delete this district?",
      showCancelButton: true,
      confirmButtonText: `Delete`,
    });
    if (decision.isConfirmed) {
      try {
        const result = await apiHelper.hkiff.deleteDistrictById(id);
        if (result.data.code === 0) {
          history.push(`/hkiff/region-management/district`);
          toast.dark("District deleted.");
        }
      } catch (err) {
        console.log(err);
      }
    }
  }, []);

  function RegionOptions(props) {
    const { regionList } = props;
    return regionList.map((value, index) => {
      return <option value={value.id}>{value.name}</option>;
    });
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {`District Management > ${id === "new" ? "Add" : "Edit"} District`}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={disabled} className="fieldset">
          <Grid container xs={8} direction="row" spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="localizations.en.name"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="District Name (English)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.tc.name"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="District Name (Traditional Chinese)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="regionId"
                control={control}
                rules={{
                  required: "Region is required",
                  validate: (v) =>
                    (v !== "" && v != null) || "Region is required",
                }}
                render={({
                  field: { value, onChange, ...fieldRest },
                  fieldState: { error },
                }) => (
                  <FormControl
                    variant="outlined"
                    margin="dense"
                    className={classes.formControl}
                    style={{ width: "100%" }}
                    error={!!error}
                  >
                    <InputLabel
                      htmlFor="outlined-region-native-simple"
                      shrink
                    >
                      Region
                    </InputLabel>
                    <Select
                      native
                      {...fieldRest}
                      value={value ?? ""}
                      onChange={onChange}
                      label="Region"
                      inputProps={{
                        name: "regionId",
                        id: "outlined-region-native-simple",
                      }}
                    >
                      <option value="">Select region</option>
                      <RegionOptions regionList={regionList} />
                    </Select>
                    {error && (
                      <FormHelperText>{error.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            <Grid container item xs={12} spacing={3}>
              <Grid item xs={4}>
                <Controller
                  control={control}
                  name="status.status"
                  render={({
                    field: { value, onChange, ...rest },
                    fieldState: { error },
                  }) => (
                    <ToggleButtonGroup
                      {...rest}
                      value={value}
                      onChange={(event, newFormats) => {
                        onChange(newFormats);
                      }}
                      exclusive
                      aria-label="Status"
                      className={classes.input}
                    >
                      <ToggleButton
                        value={true}
                        aria-label="Active"
                        style={{ width: "50%" }}
                      >
                        Active
                      </ToggleButton>
                      <ToggleButton
                        value={false}
                        aria-label="Inactive"
                        style={{ width: "50%" }}
                      >
                        Inactive
                      </ToggleButton>
                    </ToggleButtonGroup>
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          <div className={classes.buttonGroup}>
            {disabled || (
              <div className={classes.buttonGroup}>
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  style={{
                    color: "white",
                    background: "#306F9D",
                    marginRight: "5px",
                    padding: "9px 16px",
                  }}
                >
                  Save Changes
                </Button>
                {match.params.id != "new" && (
                  <Button
                    variant="contained"
                    disableElevation
                    style={{
                      color: "white",
                      background: "#962013",
                      padding: "9px 16px",
                    }}
                    onClick={() => {
                      deleteDistrict(match.params.id);
                    }}
                  >
                    Delete District
                  </Button>
                )}
              </div>
            )}
          </div>
        </fieldset>
      </form>
    </Box>
  );
}

export default DistrictEditPage;
